
import * as React from 'react';
import { switchLayout, updateAvailableLayouts } from '../../operations/switchLayout';
import { layoutTypeFacet, availableLayoutsFacet, currentItemFacet } from '../../facets/ScopeFacets';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { IFacet } from '../../models/Facet';
import { ILayoutState, LayoutManager } from '../layoutManager/LayoutManager';
import { DetailsLayout, CompactDetailsLayout } from '../layouts/DetailsLayout';
import { TilesLayout } from '../layouts/TilesLayout';
import { LayoutSwitcher } from '../layoutSwitcher/LayoutSwitcher';
import { ItemsError } from '../itemsError/ItemsError';
import { ItemsEmptyFolder } from '../itemsEmptyFolder/ItemsEmptyFolder';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { connectToItemsScope } from '../decorators/connectToItemsScope';
import { DefaultDropZone } from '../defaultDropZone/DefaultDropZone';
import { itemsViewControl } from '../../controls/itemsViewControl';

/**
 * @public
 */
export interface IBaseItemsViewProps {
  layoutType: IFacet<{}> | undefined;
  switchLayout: (payload: {
    layoutType: IFacet<{}>;
  }) => void;
  updateAvailableLayouts: (payload: {
    layouts: { [layoutId: string]: boolean; };
  }) => void;
  pinnedItemsView: JSX.Element | undefined;
  additionalLayouts: JSX.Element[] | undefined;
}

/**
 * @internal
 */
export interface IBaseItemsViewState {
  layouts: {
    [key: string]: ILayoutState | undefined;
  };
  selectedLayout: IFacet<{}> | undefined;
}

/**
 * @internal
 */
export class BaseItemsView extends React.PureComponent<IBaseItemsViewProps, IBaseItemsViewState> {
  constructor(props: IBaseItemsViewProps) {
    super(props);

    this.state = {
      layouts: {},
      selectedLayout: undefined
    };
  }

  public render(): JSX.Element {
    const {
      layouts
    } = this.state;

    const {
      layoutType,
      pinnedItemsView,
      additionalLayouts
    } = this.props;

    const currentLayout = layoutType && layouts[layoutType.id];

    let firstAvailableLayout: ILayoutState | undefined;

    const componentLayout = currentLayout && currentLayout.isAvailable && currentLayout ||
      (firstAvailableLayout = Object.keys(layouts).map((key: string) => layouts[key]).filter((layout: ILayoutState | undefined) => {
        return layout && layout.isAvailable;
      })[0], firstAvailableLayout) ||
      undefined;
    const component = componentLayout && componentLayout.content;
    const layoutKey = componentLayout && componentLayout.facet.id;

    return (
      <DefaultDropZone>
        { pinnedItemsView }
        <LayoutManager key='manager' onUpdateLayouts={ this._onUpdateLayouts }>
          { this.props.children }
          <LayoutManager>
            <DetailsLayout key='detailsLayout' />
            <CompactDetailsLayout key='compactDetailsLayout' />
            <TilesLayout key='tilesLayout' />
            { additionalLayouts }
          </LayoutManager>
        </LayoutManager>
        <LayoutSwitcher key='layout' layoutKey={ layoutKey }>
          { component }
        </LayoutSwitcher>
        <ItemsError key='error' />
        <ItemsEmptyFolder key='emptyFolder' />
      </DefaultDropZone>
    );
  }

  public componentWillUpdate(nextProps: IBaseItemsViewProps, nextState: IBaseItemsViewState): void {
    const {
      layoutType
    } = nextProps;

    const {
      layouts
    } = nextState;

    const currentLayout = layoutType && layouts[layoutType.id];

    let firstAvailableLayout: ILayoutState | undefined;

    const selectedLayout = currentLayout && currentLayout.isAvailable && layoutType ||
      (firstAvailableLayout = Object.keys(layouts).map((key: string) => layouts[key]).filter((layout: ILayoutState | undefined) => {
        return layout && layout.isAvailable;
      })[0], firstAvailableLayout && firstAvailableLayout.facet) ||
      undefined;

    this.setState({
      selectedLayout: selectedLayout
    });
  }

  public componentDidUpdate(previousProps: IBaseItemsViewProps, previousState: IBaseItemsViewState): void {
    const {
      selectedLayout,
      layouts
    } = this.state;

    if (previousState.selectedLayout !== selectedLayout) {
      if (selectedLayout !== this.props.layoutType) {
        if (selectedLayout) {
          this.props.switchLayout({
            layoutType: selectedLayout
          });
        }
      }
    }

    if (previousState.layouts !== layouts) {
      const availableLayouts: typeof availableLayoutsFacet.type = {};

      for (const key in layouts) {
        if (Object.prototype.hasOwnProperty.call(layouts, key)) {
          const layoutState = layouts[key];

          if (layoutState) {
            availableLayouts[layoutState.facet.id] = layoutState.isAvailable;
          }
        }
      }

      this.props.updateAvailableLayouts({
        layouts: availableLayouts
      });
    }
  }

  @autobind
  private _onUpdateLayouts(layouts: {
    [key: string]: ILayoutState;
  }): void {
    this.setState(() => {
      return {
        layouts: layouts
      };
    });
  }
}

interface IItemsViewOwnProps {
  itemsScopeItemKey: string;
}

/**
 * @public
 */
export interface IItemsViewProps {
  // Nothing.
}

/**
 * @public
 */
export const ItemsView: React.ComponentType<IItemsViewProps> = connectToItemsScope()(
  connectToItemCache((itemCache: IItemCache, props: IItemsViewOwnProps):
    Pick<IBaseItemsViewProps, 'layoutType' | 'switchLayout' | 'updateAvailableLayouts' | 'pinnedItemsView' | 'additionalLayouts'> => {
    const {
      itemsScopeItemKey
    } = props;

    const { dispatch } = itemCache;

    const {
      currentItem,
      layoutType
    } = itemCache.demandItem({
      currentItem: currentItemFacet,
      layoutType: layoutTypeFacet
    }, itemsScopeItemKey);

    const boundSwitchLayout = (payload: {
      layoutType: IFacet<{}>;
    }) => {
      dispatch(switchLayout({
        itemsScopeItemKey: itemsScopeItemKey,
        layoutType: payload.layoutType
      }));
    };

    const boundUpdateAvailableLayouts = (payload: {
      layouts: { [layoutId: string]: boolean; };
    }) => {
      dispatch(updateAvailableLayouts({
        itemsScopeItemKey: itemsScopeItemKey,
        layouts: payload.layouts
      }));
    };

    const currentItemKey = currentItem && currentItem.itemKey;

    const controlHandler = itemCache.demandItemFacet(itemsViewControl, currentItemKey);
    const {
      pinnedItemsView,
      additionalLayouts
    } = itemsViewControl.evaluate(controlHandler)(itemCache, {
      itemsScopeItemKey: itemsScopeItemKey,
      itemKey: currentItemKey,
      pinnedItemsView: undefined,
      additionalLayouts: undefined
    });

    return {
      layoutType: layoutType,
      switchLayout: boundSwitchLayout,
      updateAvailableLayouts: boundUpdateAvailableLayouts,
      pinnedItemsView: pinnedItemsView,
      additionalLayouts: additionalLayouts
    };
  })(BaseItemsView)
);
